import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import { superAdminTooltips } from '../../../shared/config/actionHelpCatalog';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import type {
  AdminLicenseDto,
  AdminUserBulkAction,
  AdminUserBulkOutcome,
  AdminUserDto,
  AdminUserStatus,
} from '../types';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  ADMIN_USER_ROLES,
  ADMIN_USER_STATUSES,
  formatAdminRelativeTime,
  formatUserBulkResult,
  isProtectedSuperUser,
} from '../lib/adminUsers';
import {
  AdminButton,
  AdminDataTable,
  ConfirmDialog,
  StatusPill,
  useAdminToast,
  type AdminDataTableBulkContext,
  type AdminDataTableColumn,
  type AdminDataTableFilterValues,
  type AdminDataTableRowId,
  type AdminDataTableSortState,
} from './components';
import UserActions from './UserActions';
import './styles/adminUsers.css';

export interface UserTableProps {
  users: readonly AdminUserDto[];
  licenses?: readonly AdminLicenseDto[];
  onInvite?: () => void;
  onOpen?: (user: AdminUserDto) => void;
  onBulkAction?: (
    action: AdminUserBulkAction,
    ids: number[]
  ) => Promise<AdminUserBulkOutcome>;
  onResetPassword?: (userId: number) => Promise<unknown>;
  onChangeStatus?: (userId: number, status: AdminUserStatus) => Promise<unknown>;
  filterValues?: AdminDataTableFilterValues;
  onFilterValuesChange?: (filters: AdminDataTableFilterValues) => void;
  sort?: AdminDataTableSortState;
  onSortChange?: (sort: AdminDataTableSortState) => void;
  selectedIds?: readonly AdminDataTableRowId[];
  onSelectedIdsChange?: (ids: readonly AdminDataTableRowId[]) => void;
  busy?: boolean;
  now?: Date;
}

const RoleFilterOptions = ADMIN_USER_ROLES.map((role) => ({ value: role, label: role }));
const StatusFilterOptions = ADMIN_USER_STATUSES.map((status) => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1),
}));

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const statusTone = (status: AdminUserStatus) => status === 'active'
  ? 'success' as const
  : status === 'blocked' ? 'critical' as const : 'info' as const;

const roleTone = (role: AdminUserDto['role']) => role === 'Super'
  ? 'critical' as const
  : role === 'Admin' ? 'accent' as const
    : role === 'Manager' ? 'warning' as const : 'line' as const;

const ColumnHelp: React.FC<{
  label: string;
  help: Parameters<typeof HelpTooltipTrigger>[0]['help'];
}> = ({ label, help }) => (
  <HelpTooltipTrigger label={label} help={help} placement="bottom-start" />
);

interface ColumnOptions {
  now: Date;
  users: readonly AdminUserDto[];
  openActionsId: number | null;
  setOpenActionsId: React.Dispatch<React.SetStateAction<number | null>>;
  reset?: UserTableProps['onResetPassword'];
  changeStatus?: UserTableProps['onChangeStatus'];
}

const createColumns = (options: ColumnOptions): AdminDataTableColumn<AdminUserDto>[] => [
  {
    id: 'name', header: 'Name', accessor: (user) => user.username,
    headerAccessory: <ColumnHelp label="Name" help={superAdminTooltips.userNameColumn} />,
    render: (user) => <strong className="admin-user-name">{user.username}</strong>,
    filter: { type: 'text', placeholder: 'Filter name' }, width: '1.35fr',
  },
  {
    id: 'email', header: 'Email', accessor: (user) => user.email,
    headerAccessory: <ColumnHelp label="Email" help={superAdminTooltips.userEmailColumn} />,
    render: (user) => user.email
      ? <span className="admin-user-email">{user.email}</span>
      : <span className="admin-user-muted">No email</span>,
    filter: { type: 'text', placeholder: 'Filter email' }, width: '1.45fr',
  },
  {
    id: 'company', header: 'Company', accessor: (user) => user.companyName,
    headerAccessory: <ColumnHelp label="Company" help={superAdminTooltips.userCompanyColumn} />,
    render: (user) => user.companyName || <span className="admin-user-muted">No company</span>,
    filter: { type: 'text', placeholder: 'Filter company' }, width: '1.2fr',
  },
  {
    id: 'role', header: 'Role', accessor: (user) => user.role,
    headerAccessory: <ColumnHelp label="Role" help={superAdminTooltips.userRoleColumn} />,
    render: (user) => <StatusPill tone={roleTone(user.role)}>{user.role}</StatusPill>,
    filter: { type: 'select', options: RoleFilterOptions, allLabel: 'All roles' }, width: '.75fr',
  },
  {
    id: 'licenses', header: 'Licenses', accessor: (user) => user.licenseIds.length,
    headerAccessory: <ColumnHelp label="Licenses" help={superAdminTooltips.userLicensesColumn} />,
    render: (user) => <span className="admin-user-license-count">{user.licenseIds.length} assigned</span>,
    numeric: true, width: '.8fr',
  },
  {
    id: 'lastActive', header: 'Last active', accessor: (user) => user.lastActiveAt,
    headerAccessory: <ColumnHelp label="Last active" help={superAdminTooltips.userLastActiveColumn} />,
    sortValue: (user) => user.lastActiveAt ? Date.parse(user.lastActiveAt) : null,
    render: (user) => <span className={user.lastActiveAt ? '' : 'admin-user-muted'}>
      {formatAdminRelativeTime(user.lastActiveAt, options.now)}
    </span>,
    numeric: true, width: '.85fr',
  },
  {
    id: 'status', header: 'Status', accessor: (user) => user.status,
    headerAccessory: <ColumnHelp label="Status" help={superAdminTooltips.userStatusColumn} />,
    render: (user) => <StatusPill tone={statusTone(user.status)}>{titleCase(user.status)}</StatusPill>,
    filter: { type: 'select', options: StatusFilterOptions, allLabel: 'All statuses' }, width: '.75fr',
  },
  {
    id: 'actions', header: <span className="admin-user-actions-heading">Actions</span>,
    ariaLabel: 'Actions', accessor: (user) => user.id, sortable: false, width: '48px',
    render: (user) => (
      <UserActions
        user={user}
        protectedSuper={isProtectedSuperUser(user, options.users)}
        open={options.openActionsId === user.id}
        toggle={() => options.setOpenActionsId((current) => current === user.id ? null : user.id)}
        close={() => options.setOpenActionsId(null)}
        reset={options.reset}
        onChangeStatus={options.changeStatus}
      />
    ),
  },
];

interface PendingDelete {
  ids: number[];
  deselectIds: AdminDataTableBulkContext<AdminUserDto>['deselectIds'];
}

const UserTableHeader: React.FC<{
  busy: boolean;
  count: number;
  onInvite?: () => void;
}> = ({ busy, count, onInvite }) => (
  <header className="admin-users__header">
    <h2>Users <span aria-hidden="true">{count}</span></h2>
    <AdminButton variant="primary" disabled={!onInvite || busy} onClick={onInvite}>
      <AddIcon aria-hidden="true" fontSize="small" /> Invite user
    </AdminButton>
  </header>
);

const BulkDeleteDialog: React.FC<{
  busy: boolean;
  pendingDelete: PendingDelete | null;
  onRun: (
    action: AdminUserBulkAction,
    ids: number[],
    deselectIds: PendingDelete['deselectIds']
  ) => Promise<void>;
  onClose: () => void;
}> = ({ busy, pendingDelete, onRun, onClose }) => (
  <ConfirmDialog
    open={pendingDelete !== null}
    title="Delete selected users?"
    description={`${pendingDelete?.ids.length ?? 0} selected user(s) will lose account access. Their license assignments, credentials, sessions, and exclusive registrations will be removed; licenses remain owned by their companies.`}
    confirmLabel={`Delete ${pendingDelete?.ids.length ?? 0} ${pendingDelete?.ids.length === 1 ? 'user' : 'users'}`}
    confirmDisabled={busy}
    closeOnConfirm={false}
    onConfirm={async () => {
      if (!pendingDelete) {
        return;
      }
      await onRun('delete', pendingDelete.ids, pendingDelete.deselectIds);
      onClose();
    }}
    onClose={onClose}
  />
);

const UserTable: React.FC<UserTableProps> = ({
  users,
  onInvite,
  onOpen,
  onBulkAction,
  onResetPassword,
  onChangeStatus,
  filterValues,
  onFilterValuesChange,
  sort,
  onSortChange,
  selectedIds,
  onSelectedIdsChange,
  busy = false,
  now = new Date(),
}) => {
  const { showToast } = useAdminToast();
  const [openActionsId, setOpenActionsId] = React.useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<PendingDelete | null>(null);
  const [bulkSummary, setBulkSummary] = React.useState<string | null>(null);
  const columns = React.useMemo(() => createColumns({
    now, users, openActionsId, setOpenActionsId,
    reset: busy ? undefined : onResetPassword,
    changeStatus: busy ? undefined : onChangeStatus,
  }), [busy, now, onChangeStatus, onResetPassword, openActionsId, users]);

  const runBulk = async (
    action: AdminUserBulkAction,
    ids: number[],
    deselectIds: PendingDelete['deselectIds']
  ) => {
    if (!onBulkAction) {
      return;
    }
    try {
      const result = await onBulkAction(action, ids);
      deselectIds(result.succeeded);
      const refresh = result.refreshError
        ? ` Canonical refresh failed (${result.refreshError.code}).`
        : '';
      const message = `${formatUserBulkResult(action, result)}${refresh}`;
      setBulkSummary(message);
      showToast(message, {
        tone: result.failed.length > 0 || result.refreshError ? 'warning' : 'success',
      });
    } catch (error) {
      const failure = getAdminApiError(error, 'user_bulk_failed');
      const message = `Bulk action failed (${failure.code}).`;
      setBulkSummary(message);
      showToast(message, { tone: 'critical' });
    }
  };

  const bulkSlot = ({ selectedRows, deselectIds }: AdminDataTableBulkContext<AdminUserDto>) => {
    const ids = selectedRows.map((user) => user.id);
    return (
      <>
        <span className="admin-user-bulk-count"><strong>{ids.length}</strong> selected</span>
        <AdminButton disabled={busy || !onBulkAction} onClick={() => void runBulk('block', ids, deselectIds)}>Block</AdminButton>
        <AdminButton disabled={busy || !onBulkAction} onClick={() => void runBulk('unblock', ids, deselectIds)}>Unblock</AdminButton>
        <AdminButton variant="danger" disabled={busy || !onBulkAction} onClick={() => setPendingDelete({ ids, deselectIds })}>Delete</AdminButton>
      </>
    );
  };

  return (
    <section className="admin-users" data-ready={String(!busy)} data-testid="admin-users">
      <UserTableHeader busy={busy} count={users.length} onInvite={onInvite} />
      {bulkSummary && <div className="admin-user-bulk-summary" aria-live="polite">{bulkSummary}</div>}
      <AdminDataTable
        ariaLabel="Users"
        rows={users}
        columns={columns}
        getRowId={(user) => user.id}
        getRowLabel={(user) => user.username}
        selectable
        onRowActivate={busy ? undefined : onOpen}
        bulkSlot={bulkSlot}
        emptyState="Nothing matches the current filters"
        filterValues={filterValues}
        onFilterValuesChange={onFilterValuesChange}
        sort={sort}
        onSortChange={onSortChange}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
      />
      <BulkDeleteDialog
        busy={busy}
        pendingDelete={pendingDelete}
        onRun={runBulk}
        onClose={() => setPendingDelete(null)}
      />
    </section>
  );
};

export default UserTable;
